import React, { useState, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../shared/Modal';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { piAdminService } from '../../services/piAdminService';
import { submissionService } from '../../services/submissionService';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { PISystemInfoTab } from './PISystemInfoTab';
import { PISystemParametersTab } from './PISystemParametersTab';
import { PISystemRewardsTab } from './PISystemRewardsTab';

export function PISystemEditModal({ system, isOpen, onClose, onSave }) {
  const [activeTab, setActiveTab] = useState('info');
  const [discardInfoOpen, setDiscardInfoOpen] = useState(false);
  // Snapshot the info-tab values at mount so we can detect dirty state.
  // (PISystems passes key={editingSystem?.id} so this remounts per system.)
  const initialSystemDataRef = useRef({
    name: system?.name || '',
    description: system?.description || '',
    version: system?.version || '',
    net_includes_expenses: !!system?.net_includes_expenses,
    enabled_periods: system?.enabled_periods || [],
  });
  const [systemData, setSystemData] = useState(initialSystemDataRef.current);

  const [parameters, setParameters] = useState([]);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [periodCounts, setPeriodCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadParameters = useCallback(async (ctx = {}) => {
    const isCancelled = ctx.isCancelled ?? (() => false);
    if (!system?.id) return;
    try {
      setLoading(true);
      const [catalog, enabled] = await Promise.all([
        piAdminService.getPIParameters(),
        piAdminService.getSystemParameters(system.id),
      ]);
      if (isCancelled()) return;
      setParameters(catalog);
      setCheckedIds(new Set(enabled.map((row) => row.pi_parameter_id)));
    } catch (err) {
      if (isCancelled()) return;
      setError(mapSupabaseError(err));
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [system?.id]);

  useAbortableFetch(
    ({ isCancelled }) => {
      if (isOpen && system?.id) loadParameters({ isCancelled });
    },
    [isOpen, system?.id, loadParameters]
  );

  useAbortableFetch(async ({ isCancelled }) => {
    if (!isOpen || !system?.id) return;
    const counts = await submissionService.getPeriodCountsForSystem('performance', system.id);
    if (!isCancelled()) setPeriodCounts(counts);
  }, [isOpen, system?.id]);

  const toggleParameter = (parameterId) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(parameterId)) next.delete(parameterId);
      else next.add(parameterId);
      return next;
    });
  };

  const handleSaveParameters = async () => {
    try {
      setLoading(true);
      setError('');
      // Persist in catalog sort_order so the bundle form / detail modal read
      // a stable order.
      const orderedIds = parameters
        .filter((p) => checkedIds.has(p.id))
        .map((p) => p.id);
      await piAdminService.setSystemParameters(system.id, orderedIds);
      toast.success('Parameters saved');
    } catch (err) {
      setError(mapSupabaseError(err));
    } finally {
      setLoading(false);
    }
  };

  const periodsKey = (arr) => [...(arr || [])].sort().join(',');
  const isInfoDirty =
    ['name', 'description', 'version', 'net_includes_expenses'].some(
      (k) => (systemData[k] || '') !== (initialSystemDataRef.current[k] || '')
    ) || periodsKey(systemData.enabled_periods) !== periodsKey(initialSystemDataRef.current.enabled_periods);

  const handleClose = () => {
    if (isInfoDirty && !loading) setDiscardInfoOpen(true);
    else onClose();
  };

  if (!isOpen || !system) return null;

  return (
    <Modal onClose={handleClose}>
      <div className="pi-system-edit-modal">
        <h2>Edit PI System: {system.name}</h2>

        <div className="modal-tabs" role="tablist" aria-label="PI system sections">
          {[
            { id: 'info', label: 'System Info' },
            { id: 'parameters', label: 'Parameters' },
            { id: 'rewards', label: 'Rewards' },
          ].map((t) => (
            <button
              key={t.id}
              id={`pi-system-tab-${t.id}`}
              role="tab"
              type="button"
              aria-selected={activeTab === t.id}
              aria-controls={`pi-system-panel-${t.id}`}
              className={`modal-tab ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div
          className="modal-tab-content"
          role="tabpanel"
          id={`pi-system-panel-${activeTab}`}
          aria-labelledby={`pi-system-tab-${activeTab}`}
        >
          {activeTab === 'info' && (
            <PISystemInfoTab
              systemData={systemData}
              setSystemData={setSystemData}
              loading={loading}
              onSave={onSave}
              onGoToParameters={() => setActiveTab('parameters')}
              onCancel={handleClose}
              periodCounts={periodCounts}
            />
          )}
          {activeTab === 'parameters' && (
            <PISystemParametersTab
              parameters={parameters}
              checkedIds={checkedIds}
              onToggle={toggleParameter}
              onSave={handleSaveParameters}
              loading={loading}
              error={error}
            />
          )}
          {activeTab === 'rewards' && (
            <PISystemRewardsTab system={system} />
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={discardInfoOpen}
        title="Discard unsaved info-tab changes?"
        message="Parameter changes are saved immediately, but System Info edits aren't. Closing now will lose them."
        confirmText="Discard"
        cancelText="Keep editing"
        onConfirm={() => { setDiscardInfoOpen(false); onClose(); }}
        onCancel={() => setDiscardInfoOpen(false)}
      />
    </Modal>
  );
}
